import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useCohortsByCurriculum, useCreateCohort, useDeleteCohort } from "@/hooks/useCohorts";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarIcon, Plus, Lock, Unlock, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface CohortOnlyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  curriculum: { id: string; title: string };
  onDone: () => void;
}

export function CohortOnlyModal({
  open,
  onOpenChange,
  curriculum,
  onDone,
}: CohortOnlyModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: cohorts, isLoading } = useCohortsByCurriculum(
    open ? curriculum.id : null
  );
  const createMutation = useCreateCohort();
  const deleteMutation = useDeleteCohort();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [gatingEnabled, setGatingEnabled] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const resetForm = () => {
    setName("");
    setStartDate(undefined);
    setEndDate(undefined);
    setGatingEnabled(false);
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!name.trim() || !startDate) return;
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        curriculum_id: curriculum.id,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        gating_enabled: gatingEnabled,
      });
      toast({ title: "Cohort created successfully" });
      queryClient.invalidateQueries({
        queryKey: ["cohorts-by-curriculum", curriculum.id],
      });
      resetForm();
    } catch {
      toast({
        title: "Error",
        description: "Failed to create cohort.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (cohortId: string) => {
    try {
      await deleteMutation.mutateAsync(cohortId);
      toast({ title: "Cohort removed" });
      queryClient.invalidateQueries({
        queryKey: ["cohorts-by-curriculum", curriculum.id],
      });
      setConfirmingDeleteId(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove cohort.",
        variant: "destructive",
      });
      setConfirmingDeleteId(null);
    }
  };

  const hasCohorts = cohorts && cohorts.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cohorts for {curriculum.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading cohorts…</p>
          ) : hasCohorts ? (
            <div className="space-y-2">
              {cohorts.map((cohort) => (
                <div
                  key={cohort.id}
                  className="rounded-lg border p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-medium text-sm">{cohort.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {cohort.start_date
                          ? format(new Date(cohort.start_date), "MMM d, yyyy")
                          : "No start date"}
                        {cohort.end_date &&
                          ` — ${format(new Date(cohort.end_date), "MMM d, yyyy")}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          cohort.gating_enabled
                            ? "border-orange-300 text-orange-700"
                            : ""
                        )}
                      >
                        {cohort.gating_enabled ? (
                          <><Lock className="h-3 w-3 mr-1" />Gated</>
                        ) : (
                          <><Unlock className="h-3 w-3 mr-1" />Open</>
                        )}
                      </Badge>
                      <Badge
                        variant={cohort.is_active ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {cohort.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {confirmingDeleteId !== cohort.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmingDeleteId(cohort.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {confirmingDeleteId === cohort.id && (
                    <div className="flex items-center justify-between gap-2 rounded border border-destructive/30 bg-destructive/5 p-2">
                      <p className="text-xs text-destructive">
                        This will remove the cohort and all its member enrollments.
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setConfirmingDeleteId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={deleteMutation.isPending}
                          onClick={() => handleDelete(cohort.id)}
                        >
                          {deleteMutation.isPending ? "Removing…" : "Remove"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No cohorts yet. Create your first cohort below to get started.
            </p>
          )}

          {!showForm && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowForm(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {hasCohorts ? "Add Another Cohort" : "Create First Cohort"}
            </Button>
          )}

          {showForm && (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="space-y-2">
                <Label>Cohort Name</Label>
                <Input
                  placeholder="e.g. Spring 2026 Cohort"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={gatingEnabled}
                  onCheckedChange={setGatingEnabled}
                />
                <Label>Lock modules until manually unlocked</Label>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCreate}
                  disabled={
                    !name.trim() || !startDate || createMutation.isPending
                  }
                  className="flex-1"
                >
                  {createMutation.isPending ? "Creating..." : "Create Cohort"}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="border-t pt-4 space-y-1">
            <Button onClick={onDone} className="w-full">
              Done
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              You can manage cohort members and module access from the Cohorts tab.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
